import React, { useState, useEffect } from'react'
import { Link, useSearchParams, useNavigate } from'react-router-dom'
import { Mail, Loader2, CheckCircle2, AlertCircle, RefreshCw } from'lucide-react'
import { Button } from'../components/ui/Button'
import { DEFAULT_APP_HOME } from'../config/routes'
import { BRAND } from '../config/brand'

export const VerifyEmail: React.FC = () => {
 const [searchParams] = useSearchParams()
 const navigate = useNavigate()
 const token = searchParams.get('token') ||''
 
 const [verifying, setVerifying] = useState(true)
 const [success, setSuccess] = useState(false)
 const [error, setError] = useState('')

 useEffect(() => {
 const verifyEmail = async () => {
 if (!token) {
 setVerifying(false)
 setError('No verification token provided')
 return
 }

 try {
 const response = await fetch('/api/verify-email', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify({ token })
 })

 const data = await response.json()
 
 if (data.success) {
 setSuccess(true)
 setTimeout(() => navigate(DEFAULT_APP_HOME), 3000)
 } else {
 setError(data.error ||'Failed to verify email')
 }
 } catch (err) {
 setError('An error occurred during verification')
 } finally {
 setVerifying(false)
 }
 }

 verifyEmail()
 }, [token, navigate])

 // Verifying state
 if (verifying) {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default">
 <div className="text-center">
 <Loader2 className="w-12 h-12 animate-spin text-brand mx-auto mb-4" />
 <p className="text-secondary">Verifying your email...</p>
 </div>
 </div>
 )
 }

 // Success state
 if (success) {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-md">
 <div className="bg-surface rounded-2xl shadow-xl p-8 text-center">
 <div className="w-20 h-20 bg-ok/10 rounded-full flex items-center justify-center mx-auto mb-6">
 <CheckCircle2 className="w-10 h-10 text-ok" />
 </div>
 <h1 className="text-xl font-semibold text-primary mb-4">
 You're verified!
 </h1>
 <p className="text-secondary mb-6">
 Your email is confirmed. Welcome to {BRAND.assistant} — we're here with you.
 </p>
 <p className="text-sm text-muted mb-6">
 Redirecting you to Today&apos;s Focus...
 </p>
 <Link to={DEFAULT_APP_HOME}>
 <Button className="w-full bg-brand">
 Go to Today&apos;s Focus
 </Button>
 </Link>
 </div>
 </div>
 </div>
 )
 }

 // Error state
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-md">
 <div className="bg-surface rounded-2xl shadow-xl p-8 text-center">
 <div className="w-20 h-20 bg-bad/10 rounded-full flex items-center justify-center mx-auto mb-6">
 <AlertCircle className="w-10 h-10 text-bad" />
 </div>
 <h1 className="text-xl font-semibold text-primary mb-4">
 Verification Failed
 </h1>
 <p className="text-secondary mb-6">
 {error ||'The verification link is invalid or has expired.'}
 </p>
 <div className="space-y-3">
 <Link to="/login">
 <Button className="w-full bg-brand">
 Go to Login
 </Button>
 </Link>
 <p className="text-sm text-muted">
 Need a new verification email? Log in and request one from your profile.
 </p>
 </div>
 </div>
 </div>
 </div>
 )
}

// Component for requesting verification email (used in profile)
export const EmailVerificationBanner: React.FC = () => {
 const [sending, setSending] = useState(false)
 const [sent, setSent] = useState(false)
 const [error, setError] = useState('')

 const handleResend = async () => {
 setSending(true)
 setError('')

 try {
 const response = await fetch('/api/send-verification-email', {
 method:'POST',
 credentials:'include'
 })

 const data = await response.json()
 
 if (data.success) {
 setSent(true)
 } else {
 setError(data.error ||'Failed to send verification email')
 }
 } catch (err) {
 setError('An error occurred')
 } finally {
 setSending(false)
 }
 }

 if (sent) {
 return (
 <div className="bg-ok/10 border border-ok/30 rounded-xl p-4 flex items-center gap-3">
 <CheckCircle2 className="w-5 h-5 text-ok flex-shrink-0" />
 <p className="text-sm text-ok">
 Verification email sent! Check your inbox.
 </p>
 </div>
 )
 }

 return (
 <div className="bg-warn/10 border border-warn/30 rounded-xl p-4">
 <div className="flex items-start gap-3">
 <Mail className="w-5 h-5 text-warn flex-shrink-0 mt-0.5" />
 <div className="flex-1">
 <p className="text-sm font-medium text-warn">
 Verify your email address
 </p>
 <p className="text-sm text-warn mt-1">
 Please verify your email to access all features.
 </p>
 {error && (
 <p className="text-sm text-bad mt-2">{error}</p>
 )}
 </div>
 <Button
 onClick={handleResend}
 disabled={sending}
 variant="outline"
 size="sm"
 className="flex-shrink-0 border-warn text-warn hover:bg-warn/10"
 >
 {sending ? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ) : (
 <>
 <RefreshCw className="w-4 h-4 mr-1" />
 Send
 </>
 )}
 </Button>
 </div>
 </div>
 )
}

